import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Carousel } from 'react-responsive-carousel';
import './HomeD5.scss';

/* ---------- Carrusel horizontal de productos (NO es el hero) ---------- */
const RowCarousel = ({ items = [], renderItem, ariaLabel }) => {
  const trackRef = useRef(null);

  const scrollByDir = (dir) => {
    const node = trackRef.current;
    if (!node) return;
    const amount = Math.max(node.clientWidth * 0.9, 280);
    node.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };

  if (!items?.length) return null;

  return (
    <div className="row-carousel" role="region" aria-label={ariaLabel}>
      <button type="button" className="row-nav prev" aria-label="Anterior" onClick={() => scrollByDir(-1)}>‹</button>
      <div className="row-track" ref={trackRef}>
        {items.map((it, idx) => (
          <div className="row-item" key={it?.id ?? idx}>
            {renderItem(it)}
          </div>
        ))}
      </div>
      <button type="button" className="row-nav next" aria-label="Siguiente" onClick={() => scrollByDir(1)}>›</button>
    </div>
  );
};

/* ---------- Grilla vertical de productos (sin carrusel) ---------- */
const ProductsGrid = ({ items = [], renderItem, ariaLabel }) => {
  if (!items?.length) return null;
  return (
    <div className="products-grid" role="region" aria-label={ariaLabel}>
      {items.map((it, idx) => (
        <div className="products-grid__item" key={it?.id ?? idx}>
          {renderItem(it)}
        </div>
      ))}
    </div>
  );
};

/* ---------- helpers para carteles ---------- */
const getCatCode = (it) =>
  it?.category?.code ?? it?.category_code ?? it?.categoryCode ?? it?.codeCategory ?? it?.code;

const getSubCode = (it) =>
  it?.subcategory?.code ?? it?.subCategory?.code ?? it?.subcategory_code ?? it?.subCategoryCode;

const getBannerImage = (it) => it?.image ?? it?.imageHome ?? it?.banner ?? it?.url ?? '';
const getBannerImageMobile = (it) => it?.imageMobile ?? getBannerImage(it);

/* ---------- Componente principal ---------- */
const HomeD5 = ({
  cartel = [],
  sliders = [],
  categoriesHome = [],
  mostSelled = [],
  featuredProducts = [],
  renderItem,
  disableProductCarousel = false,
  heroCarouselProps = null,
}) => {
  // Calculamos la altura real del/los headers fijos para empujar el contenido
  useEffect(() => {
    const setHeaderOffset = () => {
      const topbar = document.querySelector('.top-nav, .topbar, .top-nav-container, .header-top, .header__top');
      const navbar = document.querySelector('.d7-nav, .main-nav-container-sandy, .main-nav-container');
      const total = (topbar?.offsetHeight || 0) + (navbar?.offsetHeight || 0);
      const fallback = 88;
      document.documentElement.style.setProperty('--header-total', `${total || fallback}px`);
    };
    setHeaderOffset();
    window.addEventListener('resize', setHeaderOffset);
    return () => window.removeEventListener('resize', setHeaderOffset);
  }, []);

  const redirectCartel = (i) => {
    const it = cartel?.[i];
    if (!it) return '/productos';
    const cat = getCatCode(it);
    const sub = getSubCode(it);
    if (cat && sub) return `/productos/${cat}/${sub}`;
    if (cat) return `/productos/${cat}`;
    return '/productos';
  };

  return (
    <div className="home home-textil home-d5">
      {/* HERO */}
      {!!sliders?.length && (
        <section className="hero-main-full">
          <div className="hero-image">
            <Carousel
              autoPlay
              infiniteLoop
              showThumbs={false}
              showStatus={false}
              emulateTouch
              swipeable
              dynamicHeight   /* <-- clave para que ajuste la altura al contenido */
              {...(heroCarouselProps || {})}
            >
              {sliders.map(({ id, image, isImage, text }) => (
                <div key={id} className="hero-slide">
                  {isImage ? (
                    <img src={image} alt={text || 'Portada'} />
                  ) : (
                    <video autoPlay muted loop playsInline aria-label={text || 'Video'}>
                      <source src={image} type="video/mp4" />
                    </video>
                  )}
                </div>
              ))}
            </Carousel>
          </div>
        </section>
      )}

      {/* CATEGORÍAS DESTACADAS */}
      {!!categoriesHome?.length && (
        <section className="container category-bubbles">
          <div className="bubbles">
            {categoriesHome.slice(0, 4).map(({ category }) => (
              <Link
                key={category.id}
                to={`/productos/${category.code}`}
                className="bubble"
                aria-label={category.name}
              >
                <img src={category.imageHome} alt={category.name} />
                <span>{category.name}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* BANNERS FULL-WIDTH */}
      {cartel[0] && (
        <section className="banner-bleed">
          <picture>
            <source media="(max-width: 768px)" srcSet={getBannerImageMobile(cartel[0])} />
            <img className="banner-img" src={getBannerImage(cartel[0])} alt="" />
          </picture>
          <Link className="banner-cta" to={redirectCartel(0)}>Ver colección</Link>
        </section>
      )}

      {!!mostSelled?.length && (
        <section className="container rail rail--first-products">
          {disableProductCarousel ? (
            <ProductsGrid items={mostSelled} renderItem={renderItem} ariaLabel="Más vendidos" />
          ) : (
            <RowCarousel items={mostSelled} renderItem={renderItem} ariaLabel="Más vendidos" />
          )}
        </section>
      )}

      {cartel[1] && (
        <section className="banner-bleed">
          <picture>
            <source media="(max-width: 768px)" srcSet={getBannerImageMobile(cartel[1])} />
            <img className="banner-img" src={getBannerImage(cartel[1])} alt="" />
          </picture>
          <Link className="banner-cta" to={redirectCartel(1)}>Ver colección</Link>
        </section>
      )}

      {!!featuredProducts?.length && (
        <section className="container rail rail--second-products">
          {disableProductCarousel ? (
            <ProductsGrid items={featuredProducts} renderItem={renderItem} ariaLabel="Destacados" />
          ) : (
            <RowCarousel items={featuredProducts} renderItem={renderItem} ariaLabel="Destacados" />
          )}
        </section>
      )}

      {cartel[2] && (
        <section className="banner-bleed">
          <picture>
            <source media="(max-width: 768px)" srcSet={getBannerImageMobile(cartel[2])} />
            <img className="banner-img" src={getBannerImage(cartel[2])} alt="" />
          </picture>
          <Link className="banner-cta" to={redirectCartel(2)}>Ver colección</Link>
        </section>
      )}

      {cartel[3] && (
        <section className="banner-bleed">
          <picture>
            <source media="(max-width: 768px)" srcSet={getBannerImageMobile(cartel[3])} />
            <img className="banner-img" src={getBannerImage(cartel[3])} alt="" />
          </picture>
          <Link className="banner-cta" to={redirectCartel(3)}>Ver colección</Link>
        </section>
      )}
    </div>
  );
};

export default HomeD5;
